import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, TrendingUp, TrendingDown, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function AlertsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/alerts/mine"); setItems(data.items || []); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); const t = setInterval(load, 90_000); return () => clearInterval(t); }, []);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const dismiss = async (key) => {
    try {
      await api.post("/alerts/dismiss", { key });
      setItems((p) => p.filter((a) => a.key !== key));
    } catch {}
  };

  const dismissAll = async () => {
    try {
      await api.post("/alerts/dismiss-all");
      setItems([]);
      toast.success("Tüm bildirimler temizlendi");
    } catch {}
  };

  const goto = (sym) => { setOpen(false); nav(`/stock/${sym}`); };
  const count = items.length;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        data-testid="alerts-bell-btn"
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 rounded-lg border border-bg-border hover:bg-bg-hover flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors"
      >
        <Bell size={16} />
        {count > 0 && (
          <span
            data-testid="alerts-badge"
            className="absolute -top-1 -right-1 bg-accent-yellow text-bg-base text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
          >{count > 99 ? "99+" : count}</span>
        )}
      </button>

      {open && (
        <div
          data-testid="alerts-panel"
          className="absolute right-0 mt-2 w-96 max-w-[90vw] bg-bg-panel border border-bg-border rounded-xl shadow-2xl overflow-hidden z-[80]"
        >
          <div className="px-4 py-3 border-b border-bg-border flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-text-primary">Sinyal Bildirimleri</div>
              <div className="text-[11px] text-text-secondary">İzleme listesinden canlı</div>
            </div>
            {count > 0 && (
              <button
                data-testid="alerts-dismiss-all"
                onClick={dismissAll}
                className="text-[11px] text-brand-blue hover:underline flex items-center gap-1"
              ><CheckCheck size={11} /> Tümünü temizle</button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 flex justify-center"><div className="radar-loader" /></div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center">
                <Bell size={22} className="text-text-secondary mx-auto mb-2 opacity-60" />
                <div className="text-sm text-text-primary font-medium">Aktif sinyal yok</div>
                <div className="text-xs text-text-secondary mt-1">İzleme listenize hisse ekleyerek sinyalleri buradan görün.</div>
              </div>
            ) : items.map((a) => {
              const Icon = a.tone === "up" ? TrendingUp : TrendingDown;
              return (
                <div
                  key={a.key}
                  className="px-4 py-3 border-b border-bg-border hover:bg-bg-hover transition-colors flex items-start gap-3"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    a.tone === "up" ? "bg-signal-up/15 text-signal-up" : "bg-signal-down/15 text-signal-down"
                  }`}>
                    <Icon size={14} />
                  </div>
                  <button onClick={() => goto(a.symbol)} className="flex-1 text-left min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono font-bold text-text-primary text-sm">{a.symbol}</span>
                      <span className={`text-xs font-mono ${a.pct_change >= 0 ? "text-signal-up" : "text-signal-down"}`}>
                        {a.pct_change > 0 ? "+" : ""}{a.pct_change?.toFixed(2)}%
                      </span>
                    </div>
                    <div className="text-xs text-text-secondary mt-0.5 truncate">{a.label}</div>
                  </button>
                  <button
                    data-testid={`alert-dismiss-${a.key}`}
                    onClick={(e) => { e.stopPropagation(); dismiss(a.key); }}
                    className="text-text-secondary hover:text-signal-down flex-shrink-0"
                  ><X size={13} /></button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
